import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';

import { cn } from '@Utils/cn';

import LayerChip from '../LayerChip';
import { getBlockIcon } from '../meta';
import type { BlockNode, BlockRegistry, LayerTreeCallbacks } from '../types';
import {
  findBlock,
  findParentBlock,
  getBlockLabel,
  isContainer,
} from '../utils';

interface LayerTreeProps extends LayerTreeCallbacks {
  /** The slot's `region` root. Its children are the top-level layers. */
  root: BlockNode;
  registry: BlockRegistry;
  selectedId?: string;
}

interface BranchProps extends LayerTreeCallbacks {
  node: BlockNode;
  registry: BlockRegistry;
  selectedId?: string;
  depth: number;
  /** True when an ancestor is hidden, so this subtree is already dimmed. */
  ancestorHidden?: boolean;
}

function LayerBranch({
  node,
  registry,
  selectedId,
  depth,
  ancestorHidden = false,
  ...callbacks
}: BranchProps) {
  const container = isContainer(node, registry);
  const def = registry[node.type];
  const selected = node.id === selectedId;
  /** True when this node or anything inside it is selected. */
  const containsSelection =
    selectedId !== undefined && Boolean(findBlock(node, selectedId));
  const atCapacity =
    def?.maxChildren !== undefined && node.children.length >= def.maxChildren;
  const canAddChild =
    container &&
    containsSelection &&
    !atCapacity &&
    node.children.every((c) => c.locked?.addSiblings !== false);

  // The whole branch is the sortable unit, so dragging a container brings its
  // subtree along. The grip inside the chip is the drag handle.
  const reorderable = node.locked?.reorder !== false;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.id, disabled: !reorderable });

  return (
    // Selecting a container highlights its entire branch; selecting a block
    // inside a group tints the group a lighter gray so the selected chip's
    // darker highlight still reads on top of it.
    <div
      ref={setNodeRef}
      style={{
        transform: transform
          ? `translate(${transform.x}px, ${transform.y}px)`
          : undefined,
        transition: isDragging ? 'none' : transition,
      }}
      className={cn(
        selected && 'bg-zinc-100',
        !selected && containsSelection && 'bg-zinc-50',
        isDragging && 'relative z-10 bg-white opacity-60 shadow-md',
      )}
    >
      <LayerChip
        node={node}
        label={getBlockLabel(node, registry)}
        icon={getBlockIcon(node.type)}
        depth={depth}
        selected={selected}
        dragHandleProps={
          reorderable ? { ...attributes, ...listeners } : undefined
        }
        onSelect={callbacks.onSelect}
        onToggleHidden={callbacks.onToggleHidden}
        onDelete={callbacks.onDelete}
        onRename={callbacks.onRename}
      />
      {/* Sections stay collapsed until they hold the selection — either the
          container itself or a block inside it. */}
      {container && containsSelection && (
        // A hidden block's descendants are dimmed to show they don't render
        // either. Applied only at the outermost hidden node so nested
        // opacities don't compound.
        <div className={cn(node.hidden && !ancestorHidden && 'opacity-50')}>
          <SortableContext
            items={node.children.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            {node.children.map((child) => (
              <LayerBranch
                key={child.id}
                node={child}
                registry={registry}
                selectedId={selectedId}
                depth={depth + 1}
                ancestorHidden={ancestorHidden || node.hidden}
                {...callbacks}
              />
            ))}
          </SortableContext>
          {canAddChild && (
            <div className="flex justify-center py-1">
              <button
                type="button"
                aria-label="Add block"
                onClick={() => callbacks.onAddChild(node.id)}
                className="rounded-full p-1 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-600"
              >
                <Plus size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function LayerTree({
  root,
  registry,
  selectedId,
  ...callbacks
}: LayerTreeProps) {
  const sensors = useSensors(
    // A small activation distance keeps plain clicks (select, eye, trash)
    // from starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  // Collisions are computed against the dragged block's siblings only.
  // Without this, closestCenter also considers container branches — whose
  // rects span their whole subtree — so dragging a nested child near the
  // bottom of its list can suddenly resolve to the section (or a neighboring
  // top-level branch) as "closest", kicking `over` out of the child's
  // sortable context: the preview snaps back and the drop lands early.
  const collisionDetection: CollisionDetection = (args) => {
    const parent = findParentBlock(root, String(args.active.id));
    if (!parent) return closestCenter(args);
    const siblingIds = new Set(parent.children.map((c) => c.id));
    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((container) =>
        siblingIds.has(String(container.id)),
      ),
    });
  };

  // Sorting is same-parent only: a drop is applied when the dragged block and
  // the block it lands on share a parent; anything else is a no-op. All state
  // changes flow through the single onReorder callback.
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const activeParent = findParentBlock(root, String(active.id));
    const overParent = findParentBlock(root, String(over.id));
    if (!activeParent || activeParent.id !== overParent?.id) return;
    const fromIndex = activeParent.children.findIndex(
      (c) => c.id === active.id,
    );
    const toIndex = activeParent.children.findIndex((c) => c.id === over.id);
    if (fromIndex === -1 || toIndex === -1) return;
    callbacks.onReorder(activeParent.id, fromIndex, toIndex);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={root.children.map((c) => c.id)}
        strategy={verticalListSortingStrategy}
      >
        {root.children.map((node) => (
          <LayerBranch
            key={node.id}
            node={node}
            registry={registry}
            selectedId={selectedId}
            depth={0}
            {...callbacks}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}
